import { apiClient } from "../API/axiosInstance";
import { logger } from "../utils/logger";

/**
 * Service class for Label (Tag) operations
 * Frontend uses "labels", backend API uses "/tags"
 */
export class LabelsService {
  static async createLabel(name, color, organisationId, deviceIds = []) {
    if (!name) {
      return false;
    }

    try {
      const response = await apiClient.post("/tags", {
        name,
        color,
        organisation_id: organisationId,
        device_ids: deviceIds,
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        throw { status: 409 };
      }
      logger.error("Label create error:", error);
      return false;
    }
  }

  static async editLabel(labelId, data) {
    if (!labelId) {
      return false;
    }

    try {
      const response = await apiClient.put(`/tags/${labelId}`, data);
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        throw { status: 409 };
      }
      logger.error("Label update error:", error);
      return false;
    }
  }

  static async deleteLabel(labelId) {
    if (!labelId) {
      logger.error("Label delete error: No label ID provided");
      return false;
    }

    try {
      const response = await apiClient.delete(`/tags/${labelId}`);
      return response && response.status >= 200 && response.status < 300;
    } catch (error) {
      logger.error("Label delete error:", error);
      return false;
    }
  }

  static async fetchLabel(labelId) {
    if (!labelId) {
      return null;
    }

    try {
      const response = await apiClient.get(`/tags/${labelId}`);
      return response.data;
    } catch (error) {
      logger.error("Error fetching label:", error);
      return null;
    }
  }

  static async fetchLabels(organisationId) {
    try {
      const response = await apiClient.get("/tags", {
        params: organisationId ? { organisation_id: organisationId } : {},
      });
      return response.data || [];
    } catch (error) {
      logger.error("Error fetching labels:", error);
      return [];
    }
  }

  static async fetchLabelDevices(labelId) {
    if (!labelId) {
      return [];
    }

    try {
      const response = await apiClient.get(`/tags/${labelId}/devices`);
      return response.data || [];
    } catch (error) {
      logger.error("Error fetching label devices:", error);
      return [];
    }
  }

  static async attachDevice(labelId, deviceId) {
    if (!labelId || !deviceId) {
      return false;
    }

    try {
      await apiClient.post(`/tags/${labelId}/devices/${deviceId}`);
      return true;
    } catch (error) {
      logger.error("Error attaching device:", error);
      return false;
    }
  }

  static async detachDevice(labelId, deviceId) {
    if (!labelId || !deviceId) {
      return false;
    }

    try {
      await apiClient.delete(`/tags/${labelId}/devices/${deviceId}`);
      return true;
    } catch (error) {
      logger.error("Error detaching device:", error);
      return false;
    }
  }
}
